import {
  Field,
  FieldControl,
  FieldIcon,
  FieldLabel,
  Select,
  SelectGhostTrigger,
  SelectItem
} from "@prismicio/editor-ui"
import type { SelectContent } from "@prismicio/types-internal/lib/content"
import type { Select as SelectT } from "@prismicio/types-internal/lib/customtypes"

import { getFieldLabel } from "./utils"

interface Props {
  content: SelectContent | undefined
  field: SelectT
  id: string
  readOnly: boolean
  onContentChange: (content: SelectContent | undefined) => void
}

export function SelectField(props: Props) {
  const { content, field, id, readOnly, onContentChange } = props

  const { placeholder, options } = field.config ?? {}

  const canHaveEmptyValue = Boolean(options?.includes(""))

  const defaultValue = getDefaultValue(field)

  const value = getSelectValue({
    content,
    placeholder,
    defaultValue,
    canHaveEmptyValue
  })

  // The Select component handles a placeholder but it's a classical one that can't be selected in the list.
  // So we roll our own in this usage specific Prismic data component.
  // For now, we don't support empty string placeholders, only empty string options.
  const showPlaceholderItem = placeholder && defaultValue === undefined

  return (
    <Field>
      <FieldIcon name="arrowDropDownCircle" />
      <FieldLabel htmlFor={id}>{getFieldLabel(field)}</FieldLabel>
      <FieldControl>
        <Select
          id={id}
          size="large"
          color="grey"
          value={value}
          disabled={readOnly}
          renderTrigger={() => <SelectGhostTrigger id={id} />}
          onValueChange={(value) => {
            onContentChange(updateContent({ value, placeholder }))
          }}
        >
          {showPlaceholderItem && (
            <SelectItem value={placeholder}>{placeholder}</SelectItem>
          )}
          {field.config?.options?.map((option) => (
            <SelectItem key={option} value={option || emptyMarkerValue}>
              {option}
            </SelectItem>
          ))}
        </Select>
      </FieldControl>
    </Field>
  )
}

interface GetSelectValueArgs {
  content: SelectContent | undefined
  placeholder: string | undefined
  defaultValue: string | undefined
  canHaveEmptyValue: boolean
}

function getSelectValue(args: GetSelectValueArgs) {
  const { content, placeholder, defaultValue, canHaveEmptyValue } = args

  let { value } = content ?? {}

  if (value === "" && canHaveEmptyValue) return emptyMarkerValue
  if (value === "") value = undefined

  return value ?? defaultValue ?? placeholder
}

interface UpdateContentArgs {
  value: string
  placeholder: string | undefined
}

function updateContent(args: UpdateContentArgs): SelectContent | undefined {
  const { placeholder } = args
  let { value } = args

  // The placeholder is a valid value, when selecting it, reset the field.
  if (value === placeholder) return undefined
  // Revert back to what the user originally intended for their data.
  if (value === emptyMarkerValue) value = ""

  return {
    __TYPE__: "FieldContent",
    type: "Select",
    value
  }
}

// Traditionally, when a default value is set, the option to use is hardcoded to be the first one.
// Weird but a convention firmly in place in production now.
function getDefaultValue(field: SelectT): string | undefined {
  return field.config?.default_value === undefined
    ? undefined
    : field.config.options?.[0]
}

// Looks empty visually which respects the user's original intent but doesn't blow up radix's Select.
const emptyMarkerValue = " "
